import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useProduct } from "@/hooks/useProducts";
import { useLang, productPath, locProduct } from "@/lib/i18n";
import { euro } from "@/lib/api";

/** Vertical product video (9:16), downloaded only when the visitor presses play. */
export function ProductVideo({ p, testid = "product-video", className = "" }) {
  if (!p?.video) return null;
  return (
    <video data-testid={testid} controls playsInline preload="none" poster={p.video_poster || undefined} width="720" height="1280"
      aria-label={`Vidéo ${p.name}`}
      className={`aspect-[9/16] w-full rounded-sm border border-[var(--gold)]/40 bg-black object-cover ${className}`}>
      <source src={p.video} type="video/mp4" />
    </video>
  );
}

/** Homepage block: a product's video next to a short pitch linking to its page (hidden if the product or video is missing). */
export function VideoSpotlight({ slug, eyebrow, title, text, cta, reverse = false, testid }) {
  const { lang } = useLang();
  const { data } = useProduct(slug);
  const p = locProduct(data, lang);
  if (!p?.slug || !p.video) return null;
  return (
    <section data-testid={testid} className={`mx-auto grid max-w-[1440px] items-center gap-8 px-5 py-16 md:gap-14 md:px-10 ${reverse ? "md:grid-cols-[1fr_minmax(0,360px)]" : "md:grid-cols-[minmax(0,360px)_1fr]"}`}>
      <ProductVideo p={p} testid={`${testid}-video`} className={`mx-auto max-w-[360px] ${reverse ? "md:order-2" : ""}`} />
      <Link to={productPath(p.slug, lang)} data-testid={`${testid}-link`} className="group block rounded-sm border border-[var(--line)] bg-[var(--ink-2)] p-6 transition-[border-color] duration-300 hover:border-[var(--gold)] md:p-10">
        <p className="eyebrow mb-2">{eyebrow}</p>
        <h2 className="font-display text-3xl leading-tight md:text-4xl">{title}</h2>
        <p className="mt-2 text-sm text-[var(--parchment)]/85">{text}</p>
        <div className="mt-6">
          {p.compare_at ? <p className="text-sm text-[var(--muted)] line-through">{euro(p.compare_at)}</p> : null}
          <p data-testid={`${testid}-price`} className="font-display text-4xl text-[var(--gold-soft)]">{euro(p.price)}</p>
          <span className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)]">{cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
        </div>
      </Link>
    </section>
  );
}
